import { Injectable, OnDestroy } from "@angular/core";
import { SiteAppCommand } from "@fabric-types/app-site";
import { FabricDesignSystemLibCommand, FdsDialogDefaultBody } from "@fabric-types/lib-fabric-design-system";
import { ContentGroupType, ContentValue } from "@fabric-types/svc-content";
import { GeolocationShowGeofencingErrorAlertPayload, PlatformServiceCommand } from "@fabric-types/svc-platform";
import { Store } from "@ngrx/store";
import {
	CommonState,
	GEOFENCING_ERROR,
	GeofencingConfigService,
	GeoFencingErrorType,
	GeofencingSelector,
} from "@obg/ngx.common";
import { AbstractTranslatableAlertService, Jurisdiction, JurisdictionSelector } from "@obg/ngx.core";
import { TelemetryService } from "@obg/ngx.global";
import { DocumentRef, PlatformService } from "@obg/ngx.global.evo";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, auditTime, EMPTY, merge, Observable, Subscription } from "rxjs";
import { exhaustMap, filter, switchMap, take, tap } from "rxjs/operators";

import { GeofencingAlertEvent } from "../telemetry/ui-interaction-telemetry.model";

import {
	GEOFENCING_ERROR_ALERT_ID,
	GEOFENCING_TRANSLATION_KEYS,
	GTM_GEOFENCING_ALERT_SUB_AREA,
} from "./geofencing-alert.const";
import { GeolocationDialogComponent } from "./geolocation-dialog/geolocation-dialog.component";
import { WageringBlockDialogContainer } from "./wagering-block/wagering-block-dialog.container";

@Injectable({ providedIn: "root" })
export class GeofencingAlertService implements OnDestroy {
	private readonly DIALOG_ID = "geofencing-alert-dialog";
	private readonly REDIRECT_BUTTON_ID = "1";
	private listeners: { id: string; unlisten: () => void }[] = [];
	private subscription$$: Subscription = new Subscription();
	isBraJurisdiction = false;

	constructor(
		private alertService: AbstractTranslatableAlertService,
		private store: Store<CommonState>,
		private platformService: PlatformService,
		private telemetryService: TelemetryService,
		private geofencingConfigService: GeofencingConfigService,
		private docRef: DocumentRef
	) {
		setTimeout(() => {
			this.docRef.obgEventBus?.listen(
				PlatformServiceCommand.GeolocationShowGeofencingErrorAlert,
				(payload: GeolocationShowGeofencingErrorAlertPayload) => {
					const sub = this.showGeofencingErrorAlert$(
						GEOFENCING_ERROR.InvalidCountryAccess,
						payload.jurisdiction as Jurisdiction
					)
						.pipe(take(1))
						.subscribe();

					this.subscription$$.add(sub);
				}
			);
		}, 0);

		this.subscription$$.add(
			this.store
				.select(JurisdictionSelector.get)
				.pipe(
					ignoreNil(),
					take(1),
					tap((jurisdiction) => {
						this.isBraJurisdiction = jurisdiction === Jurisdiction.BRA;
					})
				)
				.subscribe()
		);
	}

	initialize$() {
		return merge(this.permissionNotification$(), this.geofencingError$());
	}

	ngOnDestroy() {
		this.subscription$$.unsubscribe();
	}

	permissionNotification$() {
		if (this.platformService.isServer) {
			return EMPTY;
		}

		return this.store.select(GeofencingSelector.showNotification).pipe(
			auditTime(2500),
			filter((showNotification) => showNotification),
			switchMap(() => {
				// Geo Location flow for Brazil is handled in Fabric so we do not proceed here
				if (this.isBraJurisdiction) {
					return EMPTY;
				}
				return this.openDialog$(
					GEOFENCING_TRANSLATION_KEYS.locationAccessTitle,
					GEOFENCING_TRANSLATION_KEYS.locationAccessText,
					"fds-spinner"
				);
			})
		);
	}

	private isListenerRegistered(listenerId: string): boolean {
		return this.listeners.some(({ id }) => id === listenerId);
	}

	private getRedirectButtonText(locationId?: string): { text: string; location: string } {
		let navigateToUrl;
		if (locationId) {
			navigateToUrl = this.geofencingConfigService.getArgentinaNavigateToUrl(locationId);
		}

		if (!navigateToUrl) {
			return {
				text: `${GEOFENCING_TRANSLATION_KEYS.redirectButtonText}`,
				location: this.geofencingConfigService.getArgentinaNavigateToUrl("default"),
			};
		}

		const formattedLocationId = locationId?.toLowerCase().replace(/ /g, ".");

		return {
			text: `${GEOFENCING_TRANSLATION_KEYS.redirectButtonText}-${formattedLocationId}`,
			location: navigateToUrl,
		};
	}

	geofencingError$() {
		return this.store.select(GeofencingSelector.isValid).pipe(
			switchMap((isValid) => {
				if (isValid === undefined) {
					return EMPTY;
				}
				if (isValid) {
					this.docRef.obgEventBus.dispatch(FabricDesignSystemLibCommand.DialogHide);
					return EMPTY;
				}

				return combineLatest([
					this.store.select(GeofencingSelector.getError),
					this.store.select(GeofencingSelector.isWageringBlockDialogEnabled),
					this.store.select(GeofencingSelector.locationId),
					this.store.select(JurisdictionSelector.get),
				]);
			}),
			ignoreNil(),
			filter(([error]) => error !== undefined),
			exhaustMap(([error, wageringBlockDialogEnabled, locationId, jurisdiction]) => {
				return wageringBlockDialogEnabled
					? this.showWageringBlockedAlert$()
					: this.showGeofencingErrorAlert$(error, locationId, jurisdiction);
			})
		);
	}

	showWageringBlockedAlert$() {
		return this.alertService
			.open$(
				{
					testId: GEOFENCING_ERROR_ALERT_ID,
					messageComponent: WageringBlockDialogContainer,
					hideClose: false,
					dialogConfig: {
						panelClass: "wagering-block-dialog",
					},
				},
				[]
			)
			.pipe(
				take(1),
				switchMap((ref) => ref.afterClosed())
			);
	}

	showGeofencingErrorAlert$(
		error: GeoFencingErrorType | undefined,
		locationId?: string | undefined,
		jurisdiction?: Jurisdiction | undefined
	) {
		const dialogMessageKey = this.getDialogMessageKey(error);

		this.telemetryService.track({
			SubArea: GTM_GEOFENCING_ALERT_SUB_AREA,
			event: GeofencingAlertEvent.GeofencingAlertSeen,
			Reason: error,
		});

		if (
			jurisdiction !== Jurisdiction.Iplyc &&
			jurisdiction !== Jurisdiction.Lotba &&
			jurisdiction !== Jurisdiction.LPCSE &&
			jurisdiction !== Jurisdiction.BRA
		) {
			return this.alertService
				.open$(
					{
						id: GEOFENCING_ERROR_ALERT_ID,
						testId: GEOFENCING_ERROR_ALERT_ID,
						title: GEOFENCING_TRANSLATION_KEYS.errorTitle,
						message: dialogMessageKey,
						redirectButtonText: GEOFENCING_TRANSLATION_KEYS.redirectButtonText,
						hideClose: false,
						customAlertComponent: GeolocationDialogComponent,
					},
					[]
				)
				.pipe(
					take(1),
					switchMap((ref) => ref.afterClosed())
				);
		}
		// having here the error of geolocation permission we show in the case of brazil the real tutorial
		if (jurisdiction === Jurisdiction.BRA) {
			this.docRef.obgEventBus.dispatch(SiteAppCommand.ShowLocationFlowContainer);
			return EMPTY;
		}

		switch (error) {
			case GEOFENCING_ERROR.InvalidArgentinePosition:
				return this.openDialog$(
					GEOFENCING_TRANSLATION_KEYS.errorTitle,
					dialogMessageKey,
					"account-modal_footer",
					error,
					this.getRedirectButtonText(locationId)
				);
			default:
				return this.openDialog$(
					GEOFENCING_TRANSLATION_KEYS.errorTitle,
					dialogMessageKey,
					"account-modal_footer",
					error
				);
		}
	}

	private openDialog$(
		titleKey: string,
		textKey: string,
		footerElement: string,
		testId?: string,
		redirectButton?: { text: string; location: string }
	) {
		return new Observable(
			this.docRef.obgEventBus.retrieve(ContentValue.getTranslations, {
				key: [titleKey, textKey, ...(redirectButton ? [redirectButton.text] : [])],
				requiredGroup: [ContentGroupType.Common, ContentGroupType.General],
			})
		).pipe(
			filter((translations) => translations && !!Object.keys(translations).length),
			take(1),
			tap((translations) => {
				this.openDialog(
					translations[titleKey],
					translations[textKey],
					footerElement,
					redirectButton ? { ...redirectButton, text: translations[redirectButton.text] } : undefined,
					testId
				);
			})
		);
	}

	private openDialog(
		title: string,
		text: string,
		footerElement: string,
		redirectButton?: { text: string; location: string },
		testId?: string
	) {
		let bodyConfig: FdsDialogDefaultBody = { message: text, buttonControlsProps: {} };

		if (redirectButton) {
			bodyConfig = {
				message: text,
				buttonControlsProps: { count: 1, labels: { 1: redirectButton.text } },
			};

			if (!this.isListenerRegistered(this.DIALOG_ID)) {
				this.listeners.push({
					id: this.DIALOG_ID,
					unlisten: this.docRef.obgEventBus.listen(
						FabricDesignSystemLibCommand.DialogControlsClick,
						({ dialogId, clickedElement }) => {
							if (dialogId === this.DIALOG_ID) {
								if (clickedElement.getAttribute("button-id") === this.REDIRECT_BUTTON_ID) {
									this.listeners.forEach(({ unlisten }) => unlisten());
									this.docRef.obgEventBus.dispatch(FabricDesignSystemLibCommand.DialogHide);

									window.location.replace(redirectButton.location);
								}
							}
						}
					),
				});
			}
		}

		this.docRef.obgEventBus.dispatch(FabricDesignSystemLibCommand.DialogShow, {
			dialogId: this.DIALOG_ID,
			testId: testId,
			header: {
				title: title,
				showBackButton: false,
				showCloseButton: true,
			},

			body: bodyConfig,

			footerElement: footerElement,

			template: "custom",
			position: "default",
			size: "medium",
			context: "surface-variant",
			cancelable: false,
		});
	}

	private getDialogMessageKey(error: GeoFencingErrorType | undefined): string {
		switch (error) {
			case GEOFENCING_ERROR.PermissionDenied:
				return GEOFENCING_TRANSLATION_KEYS.disabledText;
			case GEOFENCING_ERROR.InvalidPosition:
				return GEOFENCING_TRANSLATION_KEYS.invalidPosition;
			case GEOFENCING_ERROR.InvalidCountryAccess:
				return GEOFENCING_TRANSLATION_KEYS.invalidCountryAccess;
			case GEOFENCING_ERROR.RadarGeofencingGeneralError:
				return GEOFENCING_TRANSLATION_KEYS.generalError;
			case GEOFENCING_ERROR.RadarGeofencingErrorMultipleTries:
				return GEOFENCING_TRANSLATION_KEYS.multipleTriesError;
			case GEOFENCING_ERROR.RadarGeofencingServerError:
				return GEOFENCING_TRANSLATION_KEYS.serverError;
			case GEOFENCING_ERROR.VpnOrProxyDetected:
				return GEOFENCING_TRANSLATION_KEYS.vpnOrProxyError;
			default:
				return GEOFENCING_TRANSLATION_KEYS.errorText;
		}
	}
}
